<template>
  <b-container fluid class="mt-4">
    <h3>State-Wide Projects Dashboard</h3>
    <LoadingIndicator 
      v-if="waiters.loading"
      topic="Overview"
    />
    <BaseMessageBlock :messages="errors"></BaseMessageBlock>
    <div v-if="!waiters.loading">
      <BaseMessageBlock type="Success" :messages="messages"></BaseMessageBlock>
      <b-row>
        <b-col class="mb-3" cols="6" md="4" xl="2">
          <MetricBox title="Total Dollars Saved" :data="totalDollarsSaved" :isCurrency="true"></MetricBox>
        </b-col>
        <b-col class="mb-3" cols="6" md="4" xl="2">
          <MetricBox title="Total Days Saved" :data="totalTimeSaved"></MetricBox>
        </b-col>
        <b-col class="mb-3" cols="6" md="4" xl="2">
          <MetricBox title="Total Issues Resolved" :data="totalIssuesResolved"></MetricBox>
        </b-col>
        <b-col class="mb-3" cols="6" md="4" xl="2">
          <MetricBox title="Total Risks Mitigated" :data="totalRisksMitigated"></MetricBox>
        </b-col>
        <b-col class="mb-3" cols="6" md="4" xl="2">
          <MetricBox title="Total Innovations" :data="totalInnovations"></MetricBox>
        </b-col>
        <b-col class="mb-3" cols="6" md="4" xl="2">
          <MetricBox title="Total Efficiencies" :data="totalEfficiencies"></MetricBox>
        </b-col>
      </b-row>
      <h5>Data by District</h5>
      <b-row v-if="categories.length > 0">
        <b-col cols="12" lg="6" class="mt-4">
          <GChart
            v-if="categories.length > 0"
            type = "BarChart"
            :data="districtTimeData"
            :options="districtTimeOptions">
          </GChart>
          <GChart
            type = "PieChart"
            :data="daysSavedByDistrictData"
            :options="pieOptions('Days Saved by District', 300, '60%')">
          </GChart>
        </b-col>
        <b-col cols="12" lg="6" class="mt-4 mb-4">
          <GChart        
            type = "BarChart"
            :data="districtCostData"
            :options="districtCostOptions">
          </GChart>
        </b-col>
      </b-row>

      <h5>Data by Project</h5>
      <b-row v-if="projects.length > 0">
        <b-col cols="12" lg="6" class="mt-4">
          <GChart            
            type = "BarChart"
            :data="projectTimeData"
            :options="projectTimeOptions">
          </GChart>
        </b-col>
        <b-col cols="12" lg="6" class="mt-4 mb-4">
          <GChart
            v-if="projects.length > 0"
            type = "BarChart"
            :data="projectCostData"
            :options="projectCostOptions">
          </GChart>
        </b-col>
      </b-row>
    </div>
  </b-container>
</template>

<script>
import axios from "axios";
import { GChart } from "vue-google-charts";
import LessonsLearnedList from "@/sub-components/LessonsLearnedList.vue";
import MetricBox from "@/sub-components/MetricBox.vue";

export default {
  components: {
    GChart,
    LessonsLearnedList,
    MetricBox
  },
  data() {
    return {
      errors: [],
      messages: [],
      projects: [],
      lessonsLearned: [],
      alerts: [],
      categories: [],
      waiters: {
        loading: false
      }
    };
  },
  mounted() {
    this.waiters.loading = true;
    axios
      .get(`${this.$api_url}/users/${this.accountUser.userId}/project-overviews`,
        this.getReqConfig())
      .then(response => {
        this.setupOverview(response.data);

      })
      .catch(err => {
        this.errors.push(err);
      })
      .finally(() => {
        this.waiters.loading = false;
      });

    if (this.$route.query.setup == 'true') {
      this.messages.push({ message: "You're all set!"});
      this.messages.push({ message: "Be on the lookout for your monthly project surveys."});
      this.messages.push({ message: '<a href="https://support.microsoft.com/en-ie/office/add-recipients-of-my-email-messages-to-the-safe-senders-list-be1baea0-beab-4a30-b968-9004332336ce">For best results, add info@dashtech.app to your safe senders list.</a>'});
    }
  },
  methods: {
    setupOverview(overview) {
      this.projects = overview.projects;
      this.lessonsLearned = overview.lessonsLearned;
      this.alerts = overview.alerts;
      this.categories = overview.categoryGroups;
      this.categories.forEach((category) => {
        category.groupName = "District " + category.groupName;
      })
    },
    toCurrency(val) {
      return "$" + val.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    },
    firstThree(text) {
      let split = text.split(' ');
      return split.length < 4 ? text : split.slice(0, 3).join(' ') + "...";
    },
    getBarData(stats, name, field, fieldName, valueName, tooltipFunc) {
      const sorted = [...stats].sort((a, b) => a[field] > b[field] ? 1 : -1);
      let data = [
        [
          fieldName,
          valueName, 
          { role: 'style'}, 
          { role: 'tooltip', p: { html: true } }
        ]
      ];
      data = data.concat(sorted.map(item => {
        let val = item[field];
        let style = item[field] > 1 ? '#dc3545'
          : item[field] === 1 ? '#ffc107'
          : '#28a745';
        return [
          this.firstThree(item[name]),
          val, 
          style,
          tooltipFunc(item, name, field)
        ];
      }));
      return data;
    },
    getPieData(stats, name, field, fieldName, valueName, tooltipFunc) {
      let data = [
        [
          fieldName,
          valueName,
          { role: 'tooltip', p: { html: true } }
        ]
      ];
      //let total = stats.reduce((prev, curr) => prev + (curr[field] || 0), 0);
      data = data.concat(stats.map(item => {
        return [
          item[name],
          item[field],
          tooltipFunc(item, name, field)
        ]
      }));
      return data;
    },
    barOptions(title, height, areaLeft, chartAreaHeight) {
      let options = this.baseOptions(title, height, chartAreaHeight, false);
      options.chartArea.top = 20;
      options.chartArea.bottom = 20;
      options.chartArea.right = 50;
      options.chartArea.left = areaLeft;
      options.hAxis.format = 'percent';
      return options;
    },
    pieOptions(title, height, chartAreaHeight) {
      let options = this.baseOptions(title, height, chartAreaHeight, false);
      options.chartArea.right = 50;
      options.chartArea.left = 50;
      options.chartArea.bottom = 20;
      options.legend.position = 'right';
      // options.colors = ['#28a745', '#dc3545']
      return options;
    },
    baseOptions(title, height, chartAreaHeight, stacked) {
      if (!chartAreaHeight) {
        chartAreaHeight = '80%'
      }
      if (!height || isNaN(height)) {
        height = 300;
      }
      let options = {
        legend: {
          position: 'none'
        },
        title: title,
        height: height,
        chartArea: {
          height: chartAreaHeight
        },
        isStacked: stacked === true,
        vAxis: {
          viewWindow: {
            //max: 100
          }
        },
        hAxis: {
          minValue: 0,
        },
        tooltip: {
          isHtml: true
        },
        bar: {
          groupWidth: '80%'
        },
        animation: {
          startup: true,
          duration: 1500,
          easing: "out"
        },
      };
      if (stacked) {
        options.series = {
          0: { color: '#007bff' },
          1: { color: '#28a745' },
          2: { color: '#dc3545' }
        }
      }
      return options;
    },
    overScheduleTooltip(item, name, field) {
      return `<div style="padding: 5px 5px 5px 5px;">
                <strong>${item[name]}</strong><br/>
                Percent Behind Schedule: ${this.toPercent(item[field] - 1)}<br/>
              </div>`;
    },
    overBudgetTooltip(item, name, field) {
      return `<div style="padding: 5px 5px 5px 5px;">
                <strong>${item[name]}</strong><br/>
                Percent Over Budget: ${this.toPercent(item[field] - 1)}<br/>
              </div>`;
    },
    daysSavedTooltip(item, name, field) {
      return `<div style="padding: 5px 5px 5px 5px;">
                <strong>${item[name]}</strong><br/>
                Days Saved: ${item[field]}<br/>
              </div>`;
    },
    toPercent(num) {
      return (num * 100).toFixed(2) + "%";
    },
    heightByDataRows(data) {
      return (data.length) * 40;
    }
  },
  computed: {
    totalIssuesResolved() {
      return this.projects.reduce((prev, cur) => prev + cur.issuesClosed, 0);
    },
    totalRisksMitigated() {
      return this.projects.reduce((prev, cur) => prev + cur.risksClosed, 0);
    },
    totalInnovations() {
      return this.projects.reduce((prev, cur) => prev + cur.innovationCount, 0);
    },
    totalEfficiencies() {
      return this.projects.reduce((prev, cur) => prev + cur.efficiencyCount, 0);
    },
    totalDollarsSaved() {
      return this.projects.reduce((prev, cur) => prev + cur.totalDollarsSaved, 0);
    },
    totalTimeSaved() {
      return this.projects.reduce((prev, cur) => prev + cur.totalTimeSaved, 0);
    },
    getBudgetText() {
      return this.getBudgetRatio > 0 ? "Over Budget" 
        : this.getBudgetRatio < 0 ? "Under Budget"
        : "";
    },
    getScheduleText() {
      return this.getBudgetRatio > 0 ? "Behid Schedule" 
        : this.getBudgetRatio < 0 ? "Ahead of Schedule"
        : "";
    },
    getBudgetRatio() {
      let cost = this.summary.project.projectCost;
      let saved = this.dollarsSaved;
      return cost && saved && cost > 0 ? parseFloat((-1 * saved) / cost) : 0;
    },
    getScheduleRatio() {
      let schedule = 0;
      if (this.summary.project.startDate && this.summary.project.endDate) {
        let start = new Date(this.summary.project.startDate);
        let end = new Date(this.summary.project.endDate);
        schedule = (end.getTime() - start.getTime()) /  (1000 * 3600 * 24);       
      }
      let saved = this.daysSaved;
      return schedule && saved && schedule > 0 ? parseFloat((-1 * saved) / schedule) : 0;
    },
    currentProjects() { 
      return this.projects.filter(s => s.status === 'Open' &&
        new Date(s.startDate) <= new Date());
    },
    upcomingProjects() {
      return this.projects.filter(s => s.status === 'Open' &&
        new Date(s.startDate) > new Date());
    },
    closedProjects() {
      return this.projects.filter(s => s.status === 'Closed');
    },
    //Time Charts
    projectTimeData() {
      return this.getBarData(this.projects.filter(p => p.timeRatio != null), "projectName", "timeRatio", "Project", "% Behind Schedule", this.overScheduleTooltip);
    },
    projectTimeOptions() {
      let height = this.heightByDataRows(this.projectTimeData);
      return this.barOptions('Days Impacted by Project', height, 220)
    },
    districtTimeData(){
      return this.getBarData(this.categories.filter(p => p.avgTimeRatio != null), "groupName", "avgTimeRatio", "District", "% Behind Schedule", this.overScheduleTooltip);
    },
    districtTimeOptions() {
      let height = this.heightByDataRows(this.districtTimeData);
      return this.barOptions('Days Impacted by District', height, 100);
    },
    //Budget Charts
    projectCostData() {
      return this.getBarData(this.projects.filter(p => p.costRatio != null), "projectName", "costRatio", "Project", "% Over Budget", this.overBudgetTooltip);
    },
    projectCostOptions() {
      let height = this.heightByDataRows(this.projectCostData);
      return this.barOptions('Dollars Saved by Project', height, 220)
    },
    districtCostData(){
      return this.getBarData(this.categories.filter(p => p.avgCostRatio != null), "groupName", "avgCostRatio", "District", "% Over Budget", this.overBudgetTooltip);
    },
    districtCostOptions() {
      let height = this.heightByDataRows(this.districtCostData);
      return this.barOptions('Dollars Saved by District', height, 100);
    },
    daysSavedByDistrictData() {
      return this.getPieData(this.categories.filter(p => !isNaN(p.totalDaysSaved) && p.totalDaysSaved > 0), 'groupName', 'totalDaysSaved', 'District', 'Days Saved', this.daysSavedTooltip);
    }
  }
};
</script>
<style scoped>
</style>
